import React, { useState, useEffect, useRef } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMovers,
  setSelectedMover,
  getSelectedMover,
  getCurrentUser,
} from "@/store/mover/slice";
import MoveSearch from "@/components/MoveSearch";
import { SignOutButton, useUser } from "@clerk/clerk-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut } from "lucide-react";
import { Mover } from "@/types";
import { Toaster } from "@/components/ui/toaster";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCalendarDays,
  faCashRegister,
  faClipboardList,
  faPersonDollyEmpty,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import {
  faComment,
  faHome,
  faRocket,
  faTruck,
  faUser,
  faPlus,
  faChevronDown,
  faInbox,
  faPaperPlane,
  faSackDollar,
  faPiggyBank,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { checkMobileToken } from "@/store/session/slice";
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import NotificationsMenu from "@/components/NotificationsMenu";

const AuthenticatedLayout: React.FC = () => {
  const dispatch = useDispatch();
  const movers = useSelector(getMovers);
  const mover = useSelector(getSelectedMover);
  const inApp = useSelector(checkMobileToken);
  const [open, setOpen] = useState(false);
  const [showLinks, setShowLinks] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUser = useSelector(getCurrentUser);

  const handleMoverSelect = (selectedMover: Mover) => {
    localStorage.setItem("selectedMoverId", selectedMover.id.toString());
    dispatch(setSelectedMover(selectedMover));
    window.location.reload();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowLinks("");
      }
    }

    // Add event listener when showLinks is not empty
    if (showLinks) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLinks]);

  if (!mover) {
    return null;
  }

  if (inApp) {
    return (
      <main className={cn("flex-grow", inApp && "in-app")}>
        <Toaster />
        <Outlet />
      </main>
    );
  }

  return (
    <>
      <div
        className="border-b border-stone-500 bg-white px-4 h-12 sticky top-0 left-0 z-10 text-sm flex justify-between items-center"
        style={{ backgroundColor: "#F9F6F1" }}
      >
        <div className="flex md:hidden items-center pr-4 h-full border-r border-stone-500">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="mr-0">
                <Menu className="h-8 w-8" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <nav>
                <SidebarContent setOpen={setOpen} />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
        <div className="pl-4 md:pl-0 pr-4 border-r border-stone-500 h-full flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex justify-between items-center gap-2 md:gap-4 cursor-pointer w-auto">
                <img
                  src={mover?.logo_url}
                  alt={mover?.name}
                  className="block md:hidden h-8 hover:opacity-80 transition-opacity"
                />
                <div className="hidden md:block text-sm font-semibold">
                  {mover?.name}
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="max-h-[90vh] overflow-y-auto top-12"
            >
              {movers.map((moverItem) => (
                <DropdownMenuItem
                  key={moverItem.id}
                  onClick={() => handleMoverSelect(moverItem)}
                  className="flex items-center gap-2"
                >
                  <img
                    src={moverItem.logo_url}
                    alt={moverItem.name}
                    className="w-6 h-6 object-contain"
                  />
                  <span>{moverItem.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Link
          className="h-full"
          to={mover.business_type === "mover" ? "/move/new" : "/job/new"}
        >
          <div className="h-full w-full px-4 bg-blue-700 text-white hover:bg-blue-800 hover:text-white flex items-center gap-1 border-r border-stone-500">
            <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
            <span className="hidden md:block">
              {mover.business_type === "mover" ? "New Move" : "New Job"}
            </span>
          </div>
        </Link>
        <div className="flex-grow h-full pl-4">
          <MoveSearch placeholder="Search by name, email, phone, number, or service date (mm/dd/yyyy)" />
        </div>
        <div className="h-full px-4 flex items-center border-l border-stone-500">
          <NotificationsMenu movers={movers} />
        </div>
        {(currentUser?.admin || currentUser?.owner) && (
          <div className="h-full flex items-center px-4 border-l border-stone-500">
            <SettingsMenu />
          </div>
        )}
        <div className="h-full flex items-center pl-4 border-l border-stone-500">
          <UserProfileMenu />
        </div>
      </div>

      <header
        className="hidden md:flex border-b border-stone-500 bg-stone-100 px-6 items-center gap-8 h-12 left-0 z-0"
        style={{ backgroundColor: "#F9F6F1" }}
      >
        {mover?.mover_setup?.onboarding && (
          <div className="text-base font-bold text-amber-600 flex items-center gap-1">
            <FontAwesomeIcon icon={faRocket} className="w-3 h-3" />
            <Link to="/get-started">Start Here</Link>
          </div>
        )}

        {/* NavigationMenu for navigation - hidden on mobile */}
        <div className="hidden md:flex flex-grow items-center gap-12">
          <NavigationMenu className="border-none shadow-none gap-5 bg-transparent">
            <NavigationMenuList className="gap-4">
              {headerLinks(mover).map((link) => (
                <NavigationMenuItem key={link.name}>
                  <div className="relative">
                    <Link
                      to={link.to}
                      // onMouseEnter={() => setShowLinks(link.name)}
                      onClick={() => setShowLinks("")}
                      // onMouseLeave={() => setShowLinks("")}
                    >
                      <NavigationMenuLink className="font-medium text-black text-sm cursor-pointer p-0 bg-transparent hover:underline hover:decoration-yellow-600 hover:underline-offset-4 inline-flex items-center">
                        <div className="flex items-center gap-1">
                          {link.faIcon && (
                            <FontAwesomeIcon
                              icon={link.faIcon}
                              className="w-3 h-4"
                            />
                          )}
                          {link.name}
                          {link.count > 0 && (
                            <span className="bg-red-400 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center ml-1">
                              {link.count}
                            </span>
                          )}
                        </div>
                      </NavigationMenuLink>
                    </Link>
                    {link.subLinks && link.subLinks.length > 0 && (
                      <div ref={dropdownRef}>
                        <div
                          className={cn(
                            "absolute top-6 left-8 flex-row items-center gap-2 h-4 overflow-y-hidden border-l border-stone-400 border-dashed",
                            showLinks === link.name ? "flex" : "hidden"
                          )}
                        ></div>
                        <div
                          className={cn(
                            "absolute top-10 left-0 flex-col items-start gap-2.5 bg-stone-50 border border-stone-400 border-dashed px-3 py-2 shadow-md z-10",
                            showLinks === link.name ? "flex" : "hidden"
                          )}
                        >
                          {link.subLinks?.map((subLink) => (
                            <Link
                              to={subLink.to}
                              onClick={() => setShowLinks("")}
                            >
                              <div
                                key={subLink.name}
                                className="cursor-pointer bg-transparent flex items-center gap-1 hover:underline underline-offset-4 decoration-stone-600 hover:text-blue-800 hover:decoration-blue-800"
                              >
                                {subLink.faIcon && (
                                  <FontAwesomeIcon
                                    icon={subLink.faIcon}
                                    className="w-3 h-4"
                                  />
                                )}
                                <div className="text-sm font-medium">
                                  {subLink.name}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="justify-end flex items-center gap-4">
          {mover?.sales_faqs && (
            <div className="h-full flex items-center text-sm">
              <div className="flex items-center gap-1">
                {/* <HelpCircleIcon className="h-4 w-4" /> */}
                <Sheet>
                  <SheetTrigger className="underline underline-offset-4">
                    Sales Info
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[90vw] sm:w-[540px] max-w-[90vw] md:max-w-[540px] overflow-y-scroll"
                  >
                    <SheetHeader>
                      <SheetTitle>Sales Info</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: mover?.sales_faqs,
                        }}
                        className="w-full h-[85vh] whitespace-pre-wrap"
                        title="Sales Info"
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          )}
          {/* <Button
            data-canny-changelog
            variant="outline"
            size="icon"
            className="hidden md:flex border border-2 border-purple-600 bg-white rounded-full text-purple-600 hover:bg-purple-600 hover:text-white w-8 h-8"
          >
            <FontAwesomeIcon icon={faBoltLightning} className="w-4 h-4" />
          </Button> */}
        </div>
      </header>
      {/* <div className="py-2 px-8 bg-stone-50 border-b border-black sticky h-10 top-28 left-0 z-[1]">
        <Breadcrumbs />
      </div> */}
      <main className="flex-grow">
        <Toaster />
        <Outlet />
      </main>
    </>
  );
};

const headerLinks = (mover: Mover) => {
  const links: {
    faIcon: IconProp | null;
    name: string;
    to: string;
    count: number;
    subLinks?: {
      faIcon: IconProp | null;
      name: string;
      to: string;
      description?: string;
    }[];
  }[] = [
    { faIcon: faHome, name: "Home", to: "/dashboard", count: 0 },
    {
      faIcon: faInbox,
      name: "Inbox",
      to: "/inbox",
      count: mover?.unread_conversation_count,
    },
    { faIcon: faSackDollar, name: "Leads", to: "/leads", count: 0 },
    {
      faIcon: faTruck,
      name: "Jobs",
      to: "/moves",
      count: 0,
    },
    {
      faIcon: faCalendarDays,
      name: "Calendar",
      to: "/calendar",
      count: 0,
    },
    {
      faIcon: faPersonDollyEmpty,
      name: "Crew",
      to: "/dispatch",
      count: 0,
      subLinks: [
        {
          faIcon: faPaperPlane,
          name: "Dispatch",
          to: "/dispatch",
          description: "Dispatch crew members",
        },
        {
          faIcon: faClipboardList,
          name: "Timesheets",
          to: "/timesheets",
          description: "View timesheets",
        },
        {
          faIcon: faPiggyBank,
          name: "Payroll",
          to: "/payroll",
          description: "View payroll",
        },
      ],
    },
    {
      faIcon: faCashRegister,
      name: "Payments",
      to: "/payments",
      count: 0,
    },
    // {
    //   faIcon: null,
    //   name: "Reports",
    //   to: "/reports",
    //   count: 0,
    // },
  ];

  return links;
};

const SidebarContent: React.FC<{
  setOpen?: (open: boolean) => void;
}> = ({ setOpen }) => (
  <div className="mt-12 flex flex-col justify-start h-full">
    <Link
      to="/dashboard"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faHome} />
      Overview
    </Link>

    <Link
      to="/inbox"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faComment} />
      Inbox
    </Link>

    <Link
      to="/leads"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faUser} />
      Leads
    </Link>

    <Link
      to="/moves"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faTruck} />
      Moves
    </Link>

    <Link
      to="/calendar"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faCalendar} />
      Calendar
    </Link>

    {/* <Link
      to="/payments"
      className="flex flex-row justify-start items-center gap-2 text-sm px-4 py-2 border-b border-stone-200"
      onClick={() => setOpen && setOpen(false)}
    >
      <FontAwesomeIcon icon={faCashRegister} />
      Payments
    </Link> */}

    <div className="absolute bottom-8 left-4">
      <SignOutButton>
        <div className="flex items-center gap-1">
          <LogOut className="h-3 w-3" />
          <span className="text-sm">Sign out</span>
        </div>
      </SignOutButton>
    </div>
  </div>
);

const SettingsMenu: React.FC = () => {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="cursor-pointer" asChild>
        <FontAwesomeIcon icon={faGear} className="w-6 h-6" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48 absolute top-3 -right-6 bg-stone-50">
        <DropdownMenuLabel>Settings</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/company-information");
          }}
        >
          Company Information
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/operating-policies");
          }}
        >
          Operating Policies
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/labor-rates");
          }}
        >
          Labor Rates
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/basic-configuration");
          }}
        >
          Notifications
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/fees");
          }}
        >
          Fees
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/users");
          }}
        >
          Users
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/crew");
          }}
        >
          Crew
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/drip-campaigns");
          }}
        >
          Drip Campaigns
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/documents");
          }}
        >
          Documents
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/trucks");
          }}
        >
          Trucks
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/materials");
          }}
        >
          Materials
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/templates");
          }}
        >
          Templates
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            navigate("/mover-settings/sales");
          }}
        >
          Sales FAQs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserProfileMenu: React.FC = () => {
  const { user } = useUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex bg-stone-300 border border-stone-600 rounded-full w-7 h-7 items-center justify-center cursor-pointer">
          <span className="text-stone-800 text-xs font-semibold">
            {user?.fullName
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>
          {user?.fullName}
          <br />
          <span className="text-xs font-normal">
            {user?.emailAddresses[0].emailAddress}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <SignOutButton>
            <div className="flex items-center">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </div>
          </SignOutButton>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AuthenticatedLayout;
